import { useCallback, useEffect, useState } from 'react'
import { Box, Typography, Stack, Button, TextField, Divider, Drawer, Autocomplete, IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { Close } from '@mui/icons-material'
import { NUMBER_QUALITY, NUMBER_STATUS } from 'constants/index'
import { useQualityNumberFilters } from 'hooks/useQualityNumberFilters'
import useGetWabaList from 'queries/use-get-waba-list'

function FilterDrawer({ open, toggleDrawer }) {
  const { filters, setFilters, unsetFilters } = useQualityNumberFilters()

  const [selectedWaba, setSelectedWaba] = useState([])
  const [selectedQuality, setSelectedQuality] = useState(() => {
    if (filters.quality && filters.quality.length) {
      return NUMBER_QUALITY.filter((item) => filters.quality.includes(item.id.toString()))
    }
    return []
  })
  const [selectedStatus, setSelectedStatus] = useState(() => {
    if (filters.status && filters.status.length) {
      return NUMBER_STATUS.filter((item) => filters.status.includes(item.id.toString()))
    }
    return []
  })

  const { data: wabaList, isFetching: fecthingWaba } = useGetWabaList()

  useEffect(() => {
    if (!fecthingWaba && wabaList?.length && filters.wabaId?.length) {
      setSelectedWaba(wabaList.filter((item) => filters.wabaId.includes(item.id)))
    }
  }, [fecthingWaba, wabaList])

  const handleWabaChange = (_, newValue) => {
    setSelectedWaba(newValue)
  }

  const handleQualityChange = (_, newValue) => {
    setSelectedQuality(newValue)
  }

  const handleStatusChange = useCallback((_, newValue) => {
    setSelectedStatus(newValue)
  }, [])

  const handleClearSelectedFilters = useCallback(() => {
    setSelectedStatus([])
    setSelectedQuality([])
    setSelectedWaba([])
    unsetFilters()
  }, [unsetFilters])

  const handleApplyFilter = useCallback(() => {
    const filterParams = filters

    if (selectedStatus.length) {
      Object.assign(filterParams, { status: selectedStatus.map((item) => item.id) })
    } else {
      Object.assign(filterParams, { status: [] })
    }
    if (selectedQuality.length) {
      Object.assign(filterParams, { quality: selectedQuality.map((item) => item.id) })
    } else {
      Object.assign(filterParams, { quality: [] })
    }
    if (selectedWaba.length) {
      Object.assign(filterParams, { wabaId: selectedWaba.map((item) => item.id) })
    } else {
      Object.assign(filterParams, { wabaId: [] })
    }

    setFilters(filterParams)
    toggleDrawer()
  }, [filters, selectedQuality, selectedStatus, selectedWaba, setFilters, toggleDrawer])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer}
      data-testid="swiapeable-drawer"
      sx={{
        '& .HiTheme-Drawer-paper': {
          padding: '1.5rem',
          width: '22rem',
        },
      }}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
      }}
    >
      <Stack flexDirection="column" gap={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body" color="#666666" fontWeight="700" fontSize="2rem" padding="0 12px">
            Filtros
          </Typography>
          <IconButton onClick={toggleDrawer} data-testid="close-drawer">
            <Close />
          </IconButton>
        </Box>
        <Autocomplete
          disablePortal
          data-testid="waba-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="waba-filter"
          loading={fecthingWaba}
          options={wabaList || []}
          getOptionLabel={(option) => option?.name}
          disableCloseOnSelect
          value={selectedWaba}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              data-id="form-field-campaign"
              label="WABA"
              placeholder="Selecionar"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handleWabaChange}
        />

        <Autocomplete
          disablePortal
          data-testid="status-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="ad-filter"
          options={NUMBER_STATUS.filter((item) => item.id !== 0)}
          loading={fecthingWaba}
          getOptionLabel={(option) => option.label}
          disableCloseOnSelect
          value={selectedStatus}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              data-id="form-field-status"
              label="Status"
              placeholder="Selecionar"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handleStatusChange}
        />

        <Autocomplete
          disablePortal
          data-testid="quality-field-autocomplete"
          fullWidth
          multiple
          limitTags={1}
          id="qualitys-filter"
          options={NUMBER_QUALITY.filter((item) => item.id !== 0)}
          loading={fecthingWaba}
          disableCloseOnSelect
          value={selectedQuality}
          ChipProps={{
            'data-id': 'chip-filter',
          }}
          renderInput={(params) => (
            <TextField
              style={{ color: '#00aeef' }}
              {...params}
              variant="outlined"
              data-id="form-field-quality"
              label="Nível de qualidade"
              placeholder="Selecionar"
            />
          )}
          noOptionsText="Nenhum resultado"
          onChange={handleQualityChange}
        />
        <Divider sx={{ margin: '30px -24px 10px -24px', backgroundColor: '#cacaca' }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="text"
            sx={{ color: '#00aeef' }}
            onClick={() => {
              handleClearSelectedFilters()
            }}
            data-testid="reset-btn-action"
            disabled={fecthingWaba}
          >
            Redefinir
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#00aeef',
              '&:hover': {
                backgroundColor: '#009deb',
              },
            }}
            onClick={() => {
              handleApplyFilter()
            }}
            data-testid="apply-btn-action"
            disabled={fecthingWaba}
          >
            Aplicar
          </Button>
        </Box>
      </Stack>
    </Drawer>
  )
}

FilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
}

export default FilterDrawer
